import Axios, { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { OneElementTemplateFullScreen } from 'src/feature-app/Screen';
import { Text } from '@volkswagen-onehub/components-core';
import { MultipleCTASelection, DetalleMotivo, MultipleOptionCTAs } from 'src/components';
import { ConfirmacionNoCompra, ThankYouNoCompra } from '.';
import { useDispatch, useStore, useSelector } from 'react-redux';
import { dpsSend } from 'src/bff';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { FormDataGroup, FormInfo, OneFormState, Steps, MolecularResponse } from 'src/types';
import { FormController, useOneFormContext } from 'src/feature-app';
import { formatHeaders, formDataBuilder } from '../format-fields';
import { NoCompraFormProps } from './NoCompraController';
import { useInitializeNoCompraForm } from './use-initialize-no-compra-form';
import { addRemovedStep, getIndexInSteps, removeStep, resetStepsScreenIndex } from '../helpers/add-remove-steps';
import { CTAsMotivoNoQuiero } from 'src/feature-app/InputsValues/CTAsObjects';

export enum NoCompraNoBuscaSteps {
  Motivo,
  MasInformacion,
  Confirmacion,
}

export function NoCompraNoQuiero({ dpsData, idLead, idPerson, handleError, trackFormLoad, estado }: NoCompraFormProps) {
  const { initializeForm, handleNextStep } = useOneFormContext();
  const store = useStore();
  const dispatch = useDispatch();
  const { formData } = useSelector((state: OneFormState) => state);
  const [loadingPresubmit, setLoadingPresubmit] = useState(true);

  const { multipleChoice } = formData.fields;

  const handleDetalleMotivo = (multipleChoice: Array<string>) => {
    const { steps: actualSteps, screenIndex } = store.getState().formInfo;
    let newSteps = [...actualSteps];

    if (multipleChoice.includes('otros')) {
      const stepIndex = getIndexInSteps(newSteps, 'masInformacion');

      if (stepIndex === -1) {
        newSteps = addRemovedStep(steps, actualSteps, 'masInformacion', screenIndex);
        setTimeout(() => {
          handleNextStep();
        }, 0);
      }
    } else {
      newSteps = removeStep(newSteps, 'masInformacion');
    }

    const newStepsWithIndex = resetStepsScreenIndex(newSteps);

    dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
    dispatch({ type: 'UPDATE_LAST_STEP', payload: newStepsWithIndex[newStepsWithIndex.length - 1] });
    dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: newStepsWithIndex.length });
  };

  useEffect(() => {
    const sendPresubmit = async () => {
      const result = await sendForm(true);

      if (!result) {
        handleError();
      }
      setLoadingPresubmit(false);
    };

    sendPresubmit();
  }, []);

  const steps: Steps[] = [
    {
      title: (
        <>
          Nos encantaría saber más.{' '}
          <Text bold>
            ¿Cómo vas a moverte de ahora en{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              adelante?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={
            <MultipleOptionCTAs
              ignoreHandleClick={true}
              minSelected={1}
              ctaValues={CTAsMotivoNoQuiero}
              name="multipleChoice"
            />
          }
        />
      ),
      screenIndex: NoCompraNoBuscaSteps.Motivo,
      name: 'multipleChoice',
      outputs: ['multipleChoice'],
      outputsText: ['multipleChoice'],
    },
    {
      title: (
        <>
          <Text bold>¿Nos lo podrías concretar </Text>un poco más?
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={<DetalleMotivo CTALabel="No, eso es todo" label="Escribe tu motivo aquí" />}
        />
      ),
      screenIndex: NoCompraNoBuscaSteps.MasInformacion,
      name: 'masInformacion',
      outputs: ['masInformacion'],
      outputsText: ['masInformacion'],
    },
    {
      fields: <ConfirmacionNoCompra idPerson={idPerson} />,
      screenIndex: NoCompraNoBuscaSteps.Confirmacion,
      name: 'Confirmacion',
      outputs: ['confirmacion'],
      outputsText: ['confirmacion'],
    },
  ];

  const NuevoFormData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'VW',
      DESC_VN_VO: 'VN',
      DESC_GDPR_ADAPTED: 'true',
    },
    fields: { formName: 'no_compra-no_quiero', formType: 'otherform', pageCategory: 'Encuesta' }, // Analítica
  };

  const getFormInfo = (): {
    firstStep: Steps;
    lastStep: Steps;
    stepsHistory: number[];
    numberOfScreens: number;
    newSteps?: Steps[];
  } => {
    return {
      firstStep: steps[0],
      lastStep: steps[steps.length - 1],
      stepsHistory: [0],
      numberOfScreens: steps.length,
      newSteps: null,
    };
  };

  const generateData = (data?: Partial<FormDataGroup>) => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers } = formData;
    const { tc, today, url, device } = getParamsForm();

    return {
      'headers[TC]': tc,
      'headers[PINCODE]': dpsData.pincode.pincodeResponse.params.pincode,
      'headers[FECHA_INSERTION_DATE]': today,
      'headers[DESC_VN_VO]': 'VN',

      'headers[DESC_TOKEN]': 'VW_DDB_FORM_NOCOMPRA', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_ID_CAMPAIGN]': '7011n000000Aj7uAAC',
      'headers[DESC_FORM_DETAIL]': 'NoCompra Grabación', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_FORMOBJECTIVE]': fields.quieroContacto === 'true' ? 'Más Información' : 'Encuesta',
      'headers[DESC_DEVICETYPE]': device,
      'headers[DESC_CAMPAIGNBLOCK]': '9. VW LEM',
      'headers[DESC_BRAND]': 'VW',
      'headers[COD_FORM_ID]': fields.formName ? `vw-oneform-${fields.formName}` : `vw-oneform-${data.fields.formName}`,
      'headers[DESC_URLORIGIN]': url,

      'fields[DESC_LOPD]': 'PERMITE', // SOLO EN SUBMIT
      'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO', // SOLO EN SUBMIT
      'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
      'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
      'fields[DESC_FORM_VERSION]': fields.formName
        ? `vw-oneform-${fields.formName}-${process.env.VERSION}`
        : `vw-oneform-${data.fields.formName}-${process.env.VERSION}`,
      'fields[DESC_DEALER_CODE]': idLead.TXT_DESC_DEALER_CODE__c,
      'fields[DESC_DEALER]': idLead.DESC_Dealer_Code__c,
      'fields[DESC_AUXFIELD20]': idLead.External_Id_Climapro__c,
      'fields[DESC_AUXFIELD1]': 'No quiero',
      'fields[DESC_AUXFIELD12]': fields.multipleChoice,
      'fields[DESC_AUXFIELD19]': fields.masInformacion ? fields.masInformacion : null,
      'fields[COD_LEAD_ID]': idLead.Id,
      'fields[DESC_DATA_ENRICHMENT]': 'NO INFORMADO',

      'flags[FLAG_ISLEAD]': 'false',
      'flags[FLAG_ISPROSPECT]': 'false',
      'flags[FLAG_GENERATELEM]': 'false',
    };
  };

  const generateSubmitData = () => generateData();

  /**
   * Generamos la misma data que para el submit y eliminamos todos los campos que no son necesarios para el presubmit.
   */
  const generatePresubmitData = () => {
    const data = generateData(NuevoFormData);

    //
    delete data['headers[DESC_VN_VO]'];
    delete data['fields[DESC_TRANF_DATA_THIRD]'];
    //

    delete data['headers[PINCODE]'];
    delete data['flags[FLAG_ISLEAD]'];
    delete data['flags[FLAG_ISPROSPECT]'];
    delete data['flags[FLAG_GENERATELEM]'];
    delete data['fields[DESC_LOPD]'];
    delete data['fields[DESC_LSSI]'];
    delete data['fields[DESC_LOPD_GDPR_PERFILADO]'];
    delete data['fields[DESC_AUXFIELD19]'];
    delete data['fields[DESC_DATA_ENRICHMENT]'];

    data['fields[DESC_AUXFIELD1]'] = 'No quiero';
    data['headers[DESC_FORM_DETAIL]'] = 'NoCompra Pregrabación';
    data['headers[DESC_TOKEN]'] = 'VW_DDB_FORM_NOCOMPRA_PRE';

    return data;
  };

  const sendForm = async (isPresubmit?: boolean): Promise<boolean> => {
    return new Promise<any>(async (resolve, reject) => {
      //resolve(true);
      const data = isPresubmit ? generatePresubmitData() : generateSubmitData();

      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formData = formDataBuilder(dataParsed);

      const response = await dpsSend(formData);

      if (response.data?.content?.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const { firstStep, lastStep, loading } = useInitializeNoCompraForm(steps);

  useEffect(() => {
    const NuevoFormInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens: steps.length,
      screenIndex: firstStep.screenIndex,
      formTheme: 'main',
      notALayer: true,
      formType: 'inPage',
      step: firstStep,
      steps,
      stepsHistory: [0],
      thankYouComponent: <ThankYouNoCompra />,
    };

    if (!loading) {
      initializeForm(NuevoFormInfo, NuevoFormData, true);
      trackFormLoad();
    }
  }, [loading]);

  useEffect(() => {
    if (multipleChoice) handleDetalleMotivo(multipleChoice);
  }, [multipleChoice]);

  return loading || loadingPresubmit || !steps ? null : <FormController steps={steps} screenType="full-screen" />;
}
